/**
 * commandCenter 数据面板子模块
 *
 * 管理 KPI、智能推送、记忆、执行任务、早报等面板数据。
 */
import type { ExecutingTask, IntelligentFeed, KPI, Memory } from '@/types/bi'
import { computed, ref } from 'vue'

// ===== 数组上限常量（防止长时间运行导致内存泄漏） =====
const MAX_KPI_COUNT = 100
const MAX_FEED_COUNT = 200
const MAX_TASK_COUNT = 50
const MAX_MEMORY_COUNT = 100

/**
 * 创建数据面板子模块的状态和操作
 */
export function useDashboardSlice() {
  // ===== State =====
  const query = ref('')
  const loading = ref(false)
  const kpis = ref<KPI[]>([])
  const feeds = ref<IntelligentFeed[]>([])
  const memories = ref<Memory[]>([])
  const executingTasks = ref<ExecutingTask[]>([])

  // 早报相关数据
  const currentUser = ref('')
  const alertCount = ref(0)
  const briefingSummary = ref('')
  const briefingItems = ref<string[]>([])

  // ===== Getters =====
  const hasActiveTasks = computed(() => {
    return executingTasks.value.some(t => t.status === 'running')
  })

  // ===== Actions =====
  function setQuery(value: string) {
    query.value = value
  }

  function submitQuery() {
    if (!query.value.trim()) return
    loading.value = true
    // TODO: 调用 API
    setTimeout(() => {
      loading.value = false
      query.value = ''
    }, 2000)
  }

  function addKPI(kpi: KPI) {
    kpis.value.push(kpi)
    // 超过上限时截断，保留最新的（push 模式取尾部）
    if (kpis.value.length > MAX_KPI_COUNT) {
      kpis.value = kpis.value.slice(-MAX_KPI_COUNT)
    }
  }

  function removeKPI(id: string) {
    const index = kpis.value.findIndex(k => k.id === id)
    if (index > -1) {
      kpis.value.splice(index, 1)
    }
  }

  function addFeed(feed: IntelligentFeed) {
    feeds.value.unshift(feed)
    // 超过上限时截断，保留最新的（unshift 模式取头部）
    if (feeds.value.length > MAX_FEED_COUNT) {
      feeds.value = feeds.value.slice(0, MAX_FEED_COUNT)
    }
  }

  function removeFeed(id: string) {
    const index = feeds.value.findIndex(f => f.id === id)
    if (index > -1) {
      feeds.value.splice(index, 1)
    }
  }

  function addMemory(memory: Memory) {
    memories.value.unshift(memory)
    // 超过上限时截断，保留最新的（unshift 模式取头部）
    if (memories.value.length > MAX_MEMORY_COUNT) {
      memories.value = memories.value.slice(0, MAX_MEMORY_COUNT)
    }
  }

  function addTask(task: ExecutingTask) {
    executingTasks.value.unshift(task)
    // 超过上限时截断，保留最新的（unshift 模式取头部）
    if (executingTasks.value.length > MAX_TASK_COUNT) {
      executingTasks.value = executingTasks.value.slice(0, MAX_TASK_COUNT)
    }
  }

  function updateTaskProgress(id: string, progress: number) {
    const task = executingTasks.value.find(t => t.id === id)
    if (task) {
      task.progress = progress
      if (progress >= 100) {
        task.status = 'success'
      }
    }
  }

  return {
    // State
    query,
    loading,
    kpis,
    feeds,
    memories,
    executingTasks,
    currentUser,
    alertCount,
    briefingSummary,
    briefingItems,
    // Getters
    hasActiveTasks,
    // Actions
    setQuery,
    submitQuery,
    addKPI,
    removeKPI,
    addFeed,
    removeFeed,
    addMemory,
    addTask,
    updateTaskProgress,
  }
}
